import React, { useEffect, useState, useCallback } from 'react'; // ✅ useCallback を追加
import { useLocation } from 'react-router-dom';
import useNavigateWithBase from "../hooks/useNavigateWithBase"; // ✅ 新しいフックをインポート
import axios from 'axios';
import StatusBadge from '../components/StatusBadge';
import '../CSS/ReassignProjectsPage.css'; // ✅ CSS をインポート

const ReassignProjectsPage = () => {
    const location = useLocation();
    const navigate = useNavigateWithBase();
    const { companyCode, userId } = location.state || {}; // ✅ 受け取るデータを修正

    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [newUserId, setNewUserId] = useState("");
    const [currentOwnerId, setCurrentOwnerId] = useState(null);

    // ✅ `fetchProjects` を `useCallback` でメモ化
    const fetchProjects = useCallback(async () => {
        if (!companyCode || !userId) return;
    
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                'https://pmwqkcptnd.execute-api.ap-northeast-3.amazonaws.com/prod/api/list',
                JSON.stringify({ action: 'fetchProjects', company_code: companyCode, user_id: userId }),
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
    
            setProjects(response.data.projects || []);
            
            // 最初のプロジェクトの担当者を取得（複数ある場合は適宜修正）
            if (response.data.projects.length > 0) {
                setCurrentOwnerId(response.data.projects[0].created_by);
            }
        } catch (error) {
            console.error(`❌ プロジェクト取得エラー (userId: ${userId})`, error);
        }
    }, [companyCode, userId]);   

    // ✅ `fetchUsers` を `useCallback` でメモ化
    const fetchUsers = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                'https://pmwqkcptnd.execute-api.ap-northeast-3.amazonaws.com/prod/api/list',
                JSON.stringify({ action: 'fetchUsers', company_code: companyCode }),
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
    
            // `created_by` に一致するユーザーを除外
            const filteredUsers = response.data.users.filter(user => user.personal_id !== currentOwnerId);
            setUsers(filteredUsers);
        } catch (error) {
            console.error("❌ ユーザー取得エラー:", error);
        }
    }, [companyCode, currentOwnerId]); // `currentOwnerId` を依存関係に追加

    // ✅ `useEffect` に `fetchProjects` と `fetchUsers` を依存関係に追加
    useEffect(() => {
        fetchProjects();
        fetchUsers();
    }, [fetchProjects, fetchUsers]);

    // ✅ 案件の選択・解除
    const handleSelectProject = (projectId) => {
        setSelectedProjects(prev => prev.includes(projectId)
            ? prev.filter(id => id !== projectId) 
            : [...prev, projectId]);
    };

    // ✅ 案件の付け替え
    const handleReassignProjects = async () => {
        if (!newUserId || selectedProjects.length === 0) {
            alert("⚠️ 付け替え先のユーザーと案件を選択してください。");
            return;
        }
    
        const confirmReassign = window.confirm(`⚠️ 選択した ${selectedProjects.length} 件の案件をユーザー ${newUserId} に付け替えますか？`);
        if (!confirmReassign) return;
    
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                'https://ozq0s15jhf.execute-api.ap-northeast-3.amazonaws.com/prod/api/projects',
                JSON.stringify({
                    action: 'reassignProjects',
                    project_ids: selectedProjects,
                    new_user_id: newUserId,
                    company_code: companyCode
                }),
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
    
            alert(response.data.message);
            setSelectedProjects([]); // ✅ 付け替え後にリセット
    
            // ✅ 一覧を更新した後にページをリロード
            fetchProjects();
            setTimeout(() => {
                window.location.reload(); // ✅ ページをリロードして反映
            }, 1000); // 1秒待ってからリロード
        } catch (error) {
            console.error("❌ 付け替えエラー:", error);
            alert("付け替えに失敗しました。");
        }
    };

    return (
        <div className="reassign-container">
            <h2>案件の付け替え</h2>
            
            {/* ✅ ユーザー選択 */}
            <div className="user-select-container">
                <label className="user-select-label">付け替え先のユーザー:</label>
                <select className="user-select-dropdown" value={newUserId} onChange={(e) => setNewUserId(e.target.value)}>
                    <option value="">ユーザーを選択</option>
                    {users.map(user => (
                        <option key={user.personal_id} value={user.personal_id}>
                            {`${user.name}（${user.personal_id}）`}
                        </option>                
                    ))}
                </select>
            </div>

            {/* ✅ 案件リスト */}
            <h3>案件一覧</h3>

            <table className="data-table">
                <thead>
                    <tr>
                        <th>選択</th>
                        <th>プロジェクトID</th>
                        <th>プロジェクト名</th>
                        <th>進捗ステータス</th>
                        <th>クライアント</th>
                        <th>責任者</th>
                    </tr>
                </thead>
                <tbody>
                    {projects.map(project => (
                        <tr key={project.project_id}>
                            <td>
                                <input 
                                    type="checkbox" 
                                    checked={selectedProjects.includes(project.project_id)}
                                    onChange={() => handleSelectProject(project.project_id)}
                                />
                            </td>
                            <td>{project.project_id}</td>
                            <td>{project.project_name || project.name || '名前なし'}</td>
                            <td><StatusBadge status={project.status} /></td>
                            <td>{project.client_name}</td>
                            <td>{project.created_by}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className='reassign-button'>
                <button onClick={handleReassignProjects} disabled={selectedProjects.length === 0 || !newUserId}>
                    選択した案件を付け替え
                </button>
            </div>
            <div>
                <button type="button" className="back-button" onClick={() => navigate('/portal')}>
                    ポータルへ戻る
                </button>
            </div>
        </div>
    );
};

export default ReassignProjectsPage;
